import { useState, useEffect, useRef, useCallback } from "react";
import {
  fetchCareWork, loadCareWorkMembers, saveCareWorkCell,
  saveCareWorkRemark, removeCareWorkMember
} from "../api/careWorkApi";

const WORK_TYPES = ["주", "주R", "야", "야R", "24", "24R"];
const DOW = ["일", "월", "화", "수", "목", "금", "토"];
const TYPE_COLOR = {
  "주":  { bg: "#fff7e6", fg: "#d46b08", bd: "#ffd591" },
  "야":  { bg: "#e6f4ff", fg: "#0958d9", bd: "#91caff" },
  "24":  { bg: "#f9f0ff", fg: "#531dab", bd: "#d3adf7" },
};
const typeColor = (t) => TYPE_COLOR[String(t || "").replace("R", "")] || { bg: "#f5f5f5", fg: "#666", bd: "#d9d9d9" };

const COL_W   = 78;   // 일자 칸 폭
const DEPT_W  = 70;   // 부서 칸 폭
const NAME_W  = 96;   // 이름 칸 폭
const ROW_H   = 66;   // 행 높이

const pad2 = (n) => String(n).padStart(2, "0");

export default function CareWorkPage() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [members, setMembers] = useState([]);
  const [cells,   setCells]   = useState({});   // key: `${emp_no}_${day}`
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState("");
  const [cellModal,   setCellModal]   = useState(null); // { emp_no, name, day }
  const [memberModal, setMemberModal] = useState(null); // { emp_no, name, department, remark }
  const reqSeq = useRef(0);

  const workYm   = `${year}${pad2(month)}`;
  const lastDay  = new Date(year, month, 0).getDate();
  const days     = Array.from({ length: lastDay }, (_, i) => i + 1);
  const today    = new Date();
  const isThisMonth = today.getFullYear() === year && today.getMonth() + 1 === month;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

    // B4 가로 출력 (iframe 방식)
  const handlePrint = () => {
    if (members.length === 0) return;

    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const dowColor = (dow) => dow === 0 ? "#d4380d" : dow === 6 ? "#0958d9" : "#000";

    const headDays = days.map(d => {
      const dow = new Date(year, month - 1, d).getDay();
      return `<th class="dh" style="color:${dowColor(dow)}"><div>${d}</div><div class="dw">${DOW[dow]}</div></th>`;
    }).join("");

    const bodyRows = members.map(m => {
      const tds = days.map(d => {
        const c = cells[`${m.emp_no}_${d}`];
        const dow = new Date(year, month - 1, d).getDay();
        const bg = (dow === 0 || dow === 6) ? "background:#f5f5f5;" : "";
        if (!c) return `<td class="c" style="${bg}"></td>`;
        return `<td class="c" style="${bg}"><div>${esc(c.room) || "&nbsp;"}</div><div>${esc(c.work_time) || "&nbsp;"}</div><div class="t">${esc(c.work_type) || "&nbsp;"}</div></td>`;
      }).join("");
      return `<tr>
        <td class="nm">${esc(m.name)}</td>
        ${tds}
        <td class="cnt">${workCount(m.emp_no) || ""}</td>
        <td class="rm">${esc(m.remark)}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${year}년 ${month}월 출근부</title>
<style>
  @page { size: 364mm 257mm; margin: 8mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Malgun Gothic","맑은 고딕",sans-serif; color: #000; }
  h1 { margin: 0 0 3mm; font-size: 16pt; text-align: center; letter-spacing: 4px; }
  .sub { display: flex; justify-content: space-between; font-size: 9pt; margin-bottom: 2mm; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td { border: 0.3mm solid #000; text-align: center; vertical-align: middle; overflow: hidden; padding: 0; }
  th { background: #eee; font-size: 8pt; height: 9mm; }
  .dh { font-size: 9pt; line-height: 1.15; }
  .dw { font-size: 7pt; }
  .nm  { width: 19mm; font-size: 9.5pt; font-weight: 700; }
  .cnt { width: 11mm; font-size: 9.5pt; font-weight: 700; }
  .rm  { width: 22mm; font-size: 7pt; text-align: left; padding: 0 1mm; word-break: break-all; }
  td.c { height: 11mm; font-size: 6.5pt; line-height: 1.2; white-space: nowrap; }
  td.c .t { font-weight: 700; font-size: 7pt; }
</style></head><body>
  <h1>${year}년 ${month}월 출근부</h1>
  <div class="sub"><span>희망간병</span><span>총 ${members.length}명</span></div>
  <table>
    <thead><tr>
      <th style="width:19mm">이름</th>
      ${headDays}
      <th style="width:11mm">근무<br>일수</th>
      <th style="width:22mm">비고</th>
    </tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body></html>`;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  };

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await fetchCareWork(workYm);
      if (seq !== reqSeq.current) return; // 이전 요청 응답 무시
      if (res.success) {
        setMembers(res.data.members);
        const map = {};
        res.data.cells.forEach(c => { map[`${c.emp_no}_${c.work_day}`] = c; });
        setCells(map);
      } else showToast(`❌ ${res.message || "조회 실패"}`);
    } catch (e) {
      if (seq === reqSeq.current) showToast("❌ 조회 실패");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [workYm]);

  useEffect(() => { load(); }, [load]);

  const moveMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear()); setMonth(d.getMonth() + 1);
  };

  const handleLoadMembers = async () => {
    try {
      const res = await loadCareWorkMembers(workYm);
      if (res.success) {
        showToast(res.added > 0 ? `✅ ${res.added}명이 새로 추가되었습니다.` : "ℹ️ 새로 추가할 조합원이 없습니다.");
        load();
      } else showToast(`❌ ${res.message || "불러오기 실패"}`);
    } catch (e) { showToast("❌ 불러오기 실패"); }
  };

  const workCount = (emp_no) => days.reduce((n, d) => n + (cells[`${emp_no}_${d}`]?.work_type ? 1 : 0), 0);

  // 칸 저장 (모달에서 호출) → 성공 여부 반환
  const handleSaveCell = async (emp_no, day, value) => {
    try {
      const res = await saveCareWorkCell({ work_ym: workYm, emp_no, work_day: day, ...value });
      if (!res.success) { showToast(`❌ ${res.message || "저장 실패"}`); return false; }
      setCells(prev => {
        const next = { ...prev };
        const key = `${emp_no}_${day}`;
        if (!value.room && !value.work_time && !value.work_type) delete next[key];
        else next[key] = { emp_no, work_day: day, ...value };
        return next;
      });
      return true;
    } catch (e) { showToast("❌ 저장 실패"); return false; }
  };

  const handleSaveRemark = async (emp_no, remark) => {
    try {
      const res = await saveCareWorkRemark(workYm, emp_no, remark);
      if (!res.success) { showToast(`❌ ${res.message || "저장 실패"}`); return; }
      setMembers(prev => prev.map(m => m.emp_no === emp_no ? { ...m, remark } : m));
      setMemberModal(null);
      showToast("✅ 저장되었습니다.");
    } catch (e) { showToast("❌ 저장 실패"); }
  };

  const handleRemoveMember = async (emp_no) => {
    try {
      const res = await removeCareWorkMember(workYm, emp_no);
      if (!res.success) { showToast(`❌ ${res.message || "제외 실패"}`); return; }
      setMembers(prev => prev.filter(m => m.emp_no !== emp_no));
      setMemberModal(null);
      showToast("✅ 명단에서 제외했습니다.");
    } catch (e) { showToast("❌ 제외 실패"); }
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  const stickyBase = { position: "sticky", background: "#fff", zIndex: 2 };

  return (
    <div style={st.wrap}>
      {toast && <div style={st.toast}>{toast}</div>}

      {/* 상단: 제목 + 년/월 선택 */}
      <div style={st.topBar}>
        <h1 style={st.title}>🗓️ 출근부</h1>
        <div style={st.controls}>
          <button style={st.moveBtn} onClick={() => moveMonth(-1)} title="이전 달">◀</button>
          <select style={st.select} value={year} onChange={e => setYear(Number(e.target.value))}>
            {yearOptions.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select style={st.select} value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
          </select>
          <button style={st.moveBtn} onClick={() => moveMonth(1)} title="다음 달">▶</button>
          <button style={st.todayBtn} onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}>이번 달</button>
          <button style={st.loadBtn} onClick={handleLoadMembers}>👥 명단 불러오기</button>
          <button style={st.printBtn} onClick={handlePrint} disabled={members.length === 0}>🖨️ 출력 (B4)</button>
        </div>
      </div>

      <div style={st.subBar}>
        <span>{year}년 {month}월 · 총 {members.length}명</span>
        <span style={{ color: "#999" }}>칸을 누르면 입력창이 열려요 · 이름을 누르면 비고를 적을 수 있어요</span>
      </div>

      {/* 출근부 표 (가로/세로 스크롤) */}
      <div style={st.scroller}>
        {members.length === 0 ? (
          <div style={st.empty}>
            {loading ? "불러오는 중..." : <>이 달 출근부 명단이 없어요.<br />위의 <b>👥 명단 불러오기</b> 버튼을 눌러주세요.</>}
          </div>
        ) : (
          <table style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", width: DEPT_W + NAME_W + COL_W * lastDay + 70 + 160 }}>
            <thead>
              <tr>
                <th style={{ ...st.th, ...stickyBase, top: 0, left: 0, width: DEPT_W, zIndex: 5 }}>부서</th>
                <th style={{ ...st.th, ...stickyBase, top: 0, left: DEPT_W, width: NAME_W, zIndex: 5 }}>이름</th>
                {days.map(d => {
                  const dow = new Date(year, month - 1, d).getDay();
                  const red = dow === 0 || dow === 6;
                  const isToday = isThisMonth && today.getDate() === d;
                  return (
                    <th key={d} style={{ ...st.th, ...stickyBase, top: 0, width: COL_W, zIndex: 3,
                      color: red ? "#e5484d" : "#333", background: isToday ? "#fffbe6" : "#fff" }}>
                      <div style={{ fontSize: 15, fontWeight: 800 }}>{d}</div>
                      <div style={{ fontSize: 12 }}>{DOW[dow]}</div>
                    </th>
                  );
                })}
                <th style={{ ...st.th, ...stickyBase, top: 0, width: 70, zIndex: 3 }}>근무<br />일수</th>
                <th style={{ ...st.th, ...stickyBase, top: 0, width: 160, zIndex: 3 }}>비고</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.emp_no}>
                  <td style={{ ...st.td, ...stickyBase, left: 0, height: ROW_H, fontSize: 12, color: "#666" }}>{m.department || ""}</td>
                  <td style={{ ...st.td, ...stickyBase, left: DEPT_W, height: ROW_H, fontWeight: 700, fontSize: 15, cursor: "pointer" }}
                    onClick={() => setMemberModal({ ...m })}>{m.name}</td>
                  {days.map(d => {
                    const c = cells[`${m.emp_no}_${d}`];
                    const dow = new Date(year, month - 1, d).getDay();
                    const isToday = isThisMonth && today.getDate() === d;
                    const tc = c?.work_type ? typeColor(c.work_type) : null;
                    return (
                      <td key={d}
                        style={{ ...st.td, height: ROW_H, cursor: "pointer", padding: 2,
                          background: isToday ? "#fffbe6" : (dow === 0 || dow === 6) ? "#fafafa" : "#fff" }}
                        onClick={() => setCellModal({ emp_no: m.emp_no, name: m.name, day: d })}>
                        {c ? (
                          <div style={{ lineHeight: 1.25, textAlign: "center" }}>
                            <div style={st.cellLine}>{c.room || " "}</div>
                            <div style={st.cellLine}>{c.work_time || " "}</div>
                            <div style={{ ...st.badge, background: tc ? tc.bg : "transparent", color: tc ? tc.fg : "#bbb", borderColor: tc ? tc.bd : "transparent" }}>
                              {c.work_type || " "}
                            </div>
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                  <td style={{ ...st.td, height: ROW_H, fontWeight: 800, fontSize: 15, background: "#f8faff" }}>{workCount(m.emp_no) || ""}</td>
                  <td style={{ ...st.td, height: ROW_H, fontSize: 12, textAlign: "left", padding: "4px 8px", cursor: "pointer", color: "#555" }}
                    onClick={() => setMemberModal({ ...m })}>{m.remark || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 칸 입력 팝업 */}
      {cellModal && (
        <CellModal
          key={`${cellModal.emp_no}_${cellModal.day}`}
          year={year} month={month} lastDay={lastDay}
          target={cellModal}
          initial={cells[`${cellModal.emp_no}_${cellModal.day}`]}
          onClose={() => setCellModal(null)}
          onSave={handleSaveCell}
          onMoveDay={(day) => setCellModal(p => ({ ...p, day }))}
          showToast={showToast}
        />
      )}

      {/* 이름/비고 팝업 */}
      {memberModal && (
        <MemberModal
          member={memberModal}
          hasRecords={workCount(memberModal.emp_no) > 0}
          onClose={() => setMemberModal(null)}
          onSaveRemark={handleSaveRemark}
          onRemove={handleRemoveMember}
        />
      )}
    </div>
  );
}

// ===================== 칸 입력 팝업 =====================
function CellModal({ year, month, lastDay, target, initial, onClose, onSave, onMoveDay, showToast }) {
  const [room,     setRoom]     = useState(initial?.room || "");
  const [workTime, setWorkTime] = useState(initial?.work_time || "");
  const [workType, setWorkType] = useState(initial?.work_type || "");
  const [saving,   setSaving]   = useState(false);

  const dow = new Date(year, month - 1, target.day).getDay();

  const submit = async (value, then) => {
    if (saving) return;
    setSaving(true);
    const ok = await onSave(target.emp_no, target.day, value);
    setSaving(false);
    if (ok) then();
  };

  const current = { room: room.trim(), work_time: workTime.trim(), work_type: workType };

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <div style={st.modalHead}>
          <div>
            <div style={{ fontSize: 13, color: "#888" }}>{year}년 {month}월</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>
              {target.name} · {target.day}일 ({DOW[dow]})
            </div>
          </div>
          <button style={st.closeX} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 16 }}>
          <label style={st.fieldLabel}>
            병실
            <input style={st.bigInput} value={room} maxLength={30} autoFocus
              onChange={e => setRoom(e.target.value)} placeholder="예) 7901" />
          </label>
          <label style={st.fieldLabel}>
            출근시간
            <input style={st.bigInput} value={workTime} maxLength={30}
              onChange={e => setWorkTime(e.target.value)} placeholder="예) 07:00" />
          </label>
          <div style={st.fieldLabel}>
            구분
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 6 }}>
              {WORK_TYPES.map(t => {
                const on = workType === t;
                const tc = typeColor(t);
                return (
                  <button key={t} type="button"
                    style={{ ...st.typeBtn, background: on ? tc.fg : "#fff", color: on ? "#fff" : tc.fg, borderColor: tc.fg }}
                    onClick={() => setWorkType(on ? "" : t)}>{t}</button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={st.modalFoot}>
          <button style={st.clearBtn} disabled={saving}
            onClick={() => submit({ room: "", work_time: "", work_type: "" }, onClose)}>지우기</button>
          <div style={{ flex: 1 }} />
          <button style={st.cancelBtn} onClick={onClose}>취소</button>
          <button style={st.saveBtn} disabled={saving}
            onClick={() => submit(current, () => { showToast("✅ 저장되었습니다."); onClose(); })}>저장</button>
          {target.day < lastDay && (
            <button style={st.nextBtn} disabled={saving}
              onClick={() => submit(current, () => onMoveDay(target.day + 1))}>저장 후 다음 날 ▶</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== 이름/비고 팝업 =====================
function MemberModal({ member, hasRecords, onClose, onSaveRemark, onRemove }) {
  const [remark, setRemark] = useState(member.remark || "");
  const [confirmRemove, setConfirmRemove] = useState(false);

  return (
    <div style={st.overlay}>
      <div style={st.modal}>
        <div style={st.modalHead}>
          <div>
            <div style={{ fontSize: 13, color: "#888" }}>{member.department || "-"}</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{member.name}</div>
          </div>
          <button style={st.closeX} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "18px 22px" }}>
          <label style={st.fieldLabel}>
            비고
            <textarea style={{ ...st.bigInput, height: 90, resize: "none" }} value={remark} maxLength={500}
              onChange={e => setRemark(e.target.value)} placeholder="비고를 입력하세요" />
          </label>
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px dashed #e5e5e5" }}>
            {!confirmRemove ? (
              <button style={{ ...st.clearBtn, opacity: hasRecords ? 0.45 : 1 }} disabled={hasRecords}
                onClick={() => setConfirmRemove(true)}>이 달 명단에서 제외</button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#e5484d" }}>정말 이 달 명단에서 뺄까요?</span>
                <button style={st.clearBtn} onClick={() => onRemove(member.emp_no)}>네, 제외</button>
                <button style={st.cancelBtn} onClick={() => setConfirmRemove(false)}>아니요</button>
              </div>
            )}
            {hasRecords && <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>출근 기록이 있으면 명단에서 뺄 수 없어요.</div>}
          </div>
        </div>
        <div style={st.modalFoot}>
          <div style={{ flex: 1 }} />
          <button style={st.cancelBtn} onClick={onClose}>닫기</button>
          <button style={st.saveBtn} onClick={() => onSaveRemark(member.emp_no, remark.trim())}>저장</button>
        </div>
      </div>
    </div>
  );
}

const st = {
  wrap      : { padding: "20px 16px 32px", fontFamily: "inherit" },
  toast     : { position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)", background: "#1a1a2e", color: "#fff", padding: "10px 20px", borderRadius: 10, fontSize: 14, zIndex: 1000, boxShadow: "0 4px 16px rgba(0,0,0,0.25)" },
  topBar    : { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 10 },
  title     : { fontSize: 24, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  controls  : { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  select    : { height: 40, border: "1.5px solid #d9d9d9", borderRadius: 8, padding: "0 10px", fontSize: 15, fontFamily: "inherit", background: "#fff", cursor: "pointer" },
  moveBtn   : { height: 40, width: 40, border: "1.5px solid #d9d9d9", borderRadius: 8, background: "#fff", fontSize: 14, cursor: "pointer" },
  todayBtn  : { height: 40, padding: "0 12px", border: "1.5px solid #d9d9d9", borderRadius: 8, background: "#fff", fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  loadBtn   : { height: 40, padding: "0 16px", border: "none", borderRadius: 8, background: "#1677ff", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginLeft: 6 },
  printBtn  : { height: 40, padding: "0 16px", border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", color: "#333", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginLeft: 6 },
  subBar    : { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6, fontSize: 14, fontWeight: 600, color: "#444", margin: "6px 2px 10px" },
  scroller  : { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 12, overflow: "auto", maxHeight: "calc(100vh - 230px)", minHeight: 160 },
  empty     : { padding: "60px 20px", textAlign: "center", color: "#888", fontSize: 15, lineHeight: 1.9 },
  th        : { border: "1px solid #e0e0e0", padding: "6px 2px", fontSize: 13, fontWeight: 700, textAlign: "center", boxSizing: "border-box" },
  td        : { border: "1px solid #eee", textAlign: "center", fontSize: 13, boxSizing: "border-box", overflow: "hidden", verticalAlign: "middle" },
  cellLine  : { fontSize: 12, color: "#222", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minHeight: 15 },
  badge     : { display: "inline-block", minWidth: 30, marginTop: 2, padding: "0 6px", borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 800 },
  overlay   : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: 12 },
  modal     : { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440, maxHeight: "94vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" },
  modalHead : { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 22px", borderBottom: "1px solid #f0f0f0" },
  closeX    : { background: "none", border: "none", fontSize: 22, color: "#999", cursor: "pointer" },
  fieldLabel: { display: "flex", flexDirection: "column", gap: 6, fontSize: 15, fontWeight: 700, color: "#333" },
  bigInput  : { border: "1.5px solid #d9d9d9", borderRadius: 10, padding: "12px 14px", fontSize: 18, fontFamily: "inherit", outline: "none", width: "100%", boxSizing: "border-box" },
  typeBtn   : { height: 54, border: "2px solid", borderRadius: 12, fontSize: 20, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  modalFoot : { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "14px 22px 18px", borderTop: "1px solid #f0f0f0" },
  clearBtn  : { height: 42, padding: "0 14px", border: "1.5px solid #ff4d4f", borderRadius: 10, background: "#fff", color: "#ff4d4f", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  cancelBtn : { height: 42, padding: "0 16px", border: "1.5px solid #d9d9d9", borderRadius: 10, background: "#fff", color: "#555", fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  saveBtn   : { height: 42, padding: "0 22px", border: "none", borderRadius: 10, background: "#1677ff", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  nextBtn   : { height: 42, padding: "0 16px", border: "none", borderRadius: 10, background: "#52c41a", color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
};
